import { z } from 'zod';

// ─── Event payload schemas ──────────────────────────────────────────────────

export const VIDEO_COMPLETE_THRESHOLD = 0.95;

export const ScrollEventPayloadSchema = z.object({
  scrollDepth: z.number().min(0).max(1),
  lastPosition: z.number().min(0).max(1).optional(),
});

export const ActiveTimeEventPayloadSchema = z.object({
  deltaSeconds: z.number().min(0).max(3600),
});

export const VideoProgressEventPayloadSchema = z.object({
  blockId: z.string(),
  positionSeconds: z.number().min(0),
  durationSeconds: z.number().positive().optional(),
  completed: z.boolean().optional(),
  manualCompleted: z.boolean().optional(),
});

// ─── Discriminated union event ──────────────────────────────────────────────

export const ProgressEventSchema = z.discriminatedUnion('type', [
  z.object({
    eventId: z.string(),
    type: z.literal('scroll'),
    chapterId: z.string(),
    payload: ScrollEventPayloadSchema,
    clientTs: z.string(),
  }),
  z.object({
    eventId: z.string(),
    type: z.literal('active_time'),
    chapterId: z.string(),
    payload: ActiveTimeEventPayloadSchema,
    clientTs: z.string(),
  }),
  z.object({
    eventId: z.string(),
    type: z.literal('video_progress'),
    chapterId: z.string(),
    payload: VideoProgressEventPayloadSchema,
    clientTs: z.string(),
  }),
  z.object({
    eventId: z.string(),
    type: z.literal('chapter_exit'),
    chapterId: z.string(),
    payload: z.object({}).optional(),
    clientTs: z.string(),
  }),
]);

export type ProgressEvent = z.infer<typeof ProgressEventSchema>;

export const BatchProgressEventsSchema = z.object({
  events: z.array(ProgressEventSchema).min(1).max(100),
});

export type BatchProgressEventsDto = z.infer<typeof BatchProgressEventsSchema>;

// ─── Response ───────────────────────────────────────────────────────────────

export const ProgressIngestResponseSchema = z.object({
  processed: z.number(),
  chapterStates: z.record(z.enum(['not_started', 'in_progress', 'completed'])),
});

export type ProgressIngestResponse = z.infer<typeof ProgressIngestResponseSchema>;

// ─── Legacy export compat (used in some places) ─────────────────────────────

export const ProgressEventType = {
  scroll: 'scroll',
  active_time: 'active_time',
  video_progress: 'video_progress',
  chapter_exit: 'chapter_exit',
} as const;
export type ProgressEventType = (typeof ProgressEventType)[keyof typeof ProgressEventType];
